import { inject, Injectable } from '@angular/core';
import { HttpService } from 'services/http/http.service';
import { mapToQueryParams } from 'utils/helpers';
import { ActivatedRoute, ActivatedRouteSnapshot, Params, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';

import { ShowIfHttpError } from '@certemy/common';
import { TodoListResponse } from './todo.interface';
import { map } from 'rxjs/operators';
import { TableFilters } from './list/todo-list.constants';
import { AvailableFilters } from 'interfaces/testing-workflow.interface';
import _get from 'lodash-es/get';
import { WORKFLOW_MODULE_ID } from 'constants/workflow.constants';
import moment from 'moment-mini';

@Injectable()
export class TodoService {
  constructor(private http: HttpService, private activatedRouter: ActivatedRoute) {}

  @ShowIfHttpError('Cannot receive todo list.')
  getList(
    filters?: TableFilters,
    queryParams?: Params,
    workflowTypeId: WORKFLOW_MODULE_ID = WORKFLOW_MODULE_ID.TESTING_WORKFLOW,
    certemyTasks: boolean = false,
  ): Observable<TodoListResponse> {
    if (!filters) {
      filters = new TableFilters(workflowTypeId);
      let queryParamsFilters = _get(queryParams, 'filters', null);
      if (queryParamsFilters) {
        queryParamsFilters = JSON.parse(decodeURIComponent(queryParamsFilters));
        Object.assign(filters, {
          filters: {
            ...filters.filters,
            ...queryParamsFilters,
          },
        });
      }
    }
    const params = mapToQueryParams<TableFilters>({ filters, createOffset: true });
    if (certemyTasks) {
      return this.http.get(`/to-do/certemy-tasks${params}`).pipe(
        map(response => {
          return {
            ...response,
            rows: response.rows.map(row => {
              try {
                const stepDueDateInMoment = moment(row.stepDueDate, 'MM/DD/YYYY');
                const _todoStartDateMaxValue = (stepDueDateInMoment.utc().isBefore(moment().utc())
                  ? moment()
                  : moment(row.stepDueDate, 'MM/DD/YYYY')
                ).format('YYYY-MM-DD');
                return {
                  ...row,
                  _todoStartDateMaxValue,
                };
              } catch (e) {
                return row;
              }
            }),
          };
        }),
      );
    }
    return this.http.get(`/to-do${params}`);
  }

  @ShowIfHttpError('Cannot receive todo filters.')
  getAvailableFilters(): Observable<AvailableFilters> {
    return this.http.get('/to-do/available-filters');
  }
}

@Injectable()
export class TodoListResolverService  {
  constructor(private todoService: TodoService) {}

  resolve(route: ActivatedRouteSnapshot) {
    const workflowTypeId: WORKFLOW_MODULE_ID = route.data.workflowTypeId;
    const certemyTasks = !!route.data.certemyTasks;
    return this.todoService.getList(null, route.queryParams, workflowTypeId, certemyTasks);
  }
}

export const todoListResolver: ResolveFn<Observable<TodoListResponse>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(TodoListResolverService).resolve(route);
};

@Injectable()
export class TodoListFilterResolverService {
  constructor(private todoService: TodoService) {}

  resolve(route: ActivatedRouteSnapshot) {
    const workflowTypeId = route.data.workflowTypeId || WORKFLOW_MODULE_ID.TESTING_WORKFLOW;
    const certemyTasks = !!route.data.certemyTasks;

    if (certemyTasks) {
      return of({});
    }

    switch (workflowTypeId) {
      case WORKFLOW_MODULE_ID.TESTING_WORKFLOW:
        return this.todoService.getAvailableFilters().pipe(
          map(filtersData => {
            const keys = Object.keys(filtersData);
            return keys.reduce((acc, key) => {
              acc[key] = filtersData[key].map(value => ({ id: value, name: value }));
              return acc;
            }, {});
          }),
        );
      case WORKFLOW_MODULE_ID.COMPLIANCE:
        return of({});
    }
  }
}

export const todoListFilterResolver: ResolveFn<Observable<{}>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(TodoListFilterResolverService).resolve(route);
};
